import { FC, useCallback, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useHistory } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import GoBackHeader from 'components/organisms/GoBackHeader'
import Button from 'react-bootstrap/Button'
import {
  setSelectedDocumentId,
  setSelectedDocumentIndex,
  setSelectedCheckListDocument
} from 'reducers/document/documentProcessingSlice'
import {
  CheckListDocument,
  DocumentCheckListItemType
} from 'share/domain/document'
import {
  GET_CHECKLISTITEM_DOCUMENTS,
  GetChecklistItemDocumentsQueryResponse,
  GetChecklistItemDocumentsQueryVariables
} from 'share/queries/documents'

import ContentNotAvailable from 'components/atoms/ContentNotAvailable'

import {
  DocumentsFolderContainer,
  DocumentListEmptyContainer,
  DocumentsFolderList,
  DocumentsFolderListItem,
  DocumentsFolderListItemContent,
  DocumentsFolderListItemIcon
} from './styles'

const getStatus = (status: string, withIcon = false): string => {
  switch (status.toLowerCase()) {
    case 'pending':
      return `${withIcon && 'mdi mdi-clock-outline'} yellow`
    case 'rejected':
      return `${withIcon && 'mdi mdi-alert-rhombus-outline'} red`
    case 'accepted':
      return `${withIcon && 'mdi mdi-check-circle-outline'} green`
    default:
      return ''
  }
}

interface IHistory {
  navFromCourses: boolean
  businessObjectId: number | null
  businessObjectType: string
}

const AiDocumentsChecklistItem: FC = () => {
  const history = useHistory<IHistory>()
  const dispatch = useAppDispatch()
  const [documents, setDocuments] = useState<CheckListDocument[]>([])
  const [goBackHeaderName, setGoBackHeaderName] = useState('')
  const [showSummaryButton, setShowSummaryButton] = useState(false)
  const [showErrorIcon, setShowErrorIcon] = useState(false)

  const caseData = useAppSelector((state) => state.activeCase)

  const documentProcessing = useAppSelector((state) => state.documentProcessing)

  const { data, loading, error } = useQuery<
    GetChecklistItemDocumentsQueryResponse,
    GetChecklistItemDocumentsQueryVariables
  >(GET_CHECKLISTITEM_DOCUMENTS, {
    variables: {
      request: {
        caseId: caseData?.matter?.id || 0,
        documentCheckListItemId:
          documentProcessing?.selectedDocumentChecklistItemId || 0,
        documentCheckListItemName:
          documentProcessing?.selectedDocumentChecklistItem?.name || '',
        documentCheckListItemStatus:
          documentProcessing?.selectedDocumentChecklistItem?.status || ''
      }
    }
  })

  useEffect(() => {
    setGoBackHeaderName(
      documentProcessing.selectedDocumentChecklistItem?.name
        ? documentProcessing.selectedDocumentChecklistItem?.name
        : ''
    )
  }, [])

  useEffect(() => {
    if (
      data &&
      data?.documents.checkListItemDocuments.documentList.length > 0
    ) {
      setDocuments(data?.documents.checkListItemDocuments.documentList)
    }
  }, [data])

  useEffect(() => {
    if (
      documentProcessing &&
      documentProcessing?.selectedDocumentChecklistItem
    ) {
      if (
        documentProcessing.selectedDocumentChecklistItem.documentItemTypeId ===
          DocumentCheckListItemType.BankAccountStatements ||
        documentProcessing.selectedDocumentChecklistItem.documentItemTypeId ===
          DocumentCheckListItemType.FederalTaxReturns ||
        documentProcessing.selectedDocumentChecklistItem.documentItemTypeId ===
          DocumentCheckListItemType.Paystubs
      ) {
        if (
          documentProcessing.selectedDocumentChecklistItem.status &&
          documentProcessing.selectedDocumentChecklistItem.status !==
            'In Progress'
        )
          setShowSummaryButton(true)
      }

      if (documentProcessing.selectedDocumentChecklistItem.hasWarnings) {
        setShowErrorIcon(true)
      }
    }
  }, [documentProcessing])

  const renderDocument = useCallback(
    (document: CheckListDocument, index: number) => {
      return (
        <DocumentsFolderListItem
          key={document.matterDocumentId}
          onClick={() => {
            dispatch(setSelectedDocumentId(document.matterDocumentId))
            dispatch(setSelectedDocumentIndex(index))
            dispatch(setSelectedCheckListDocument(document))
            history.push('/dashboard/documents/detail')
          }}
        >
          <DocumentsFolderListItemIcon>
            <span className='mdi mdi-file-pdf' />
          </DocumentsFolderListItemIcon>
          <DocumentsFolderListItemContent>
            <p>{document.name}</p>
            <span>Uploaded: {document.uploaded}</span>
            <span className={getStatus(document.status ?? '')}>
              {document.status}
            </span>
            <p>{document.comments}</p>
          </DocumentsFolderListItemContent>
          <span className='mdi mdi-chevron-right' />
        </DocumentsFolderListItem>
      )

      return null
    },
    [document]
  )

  const renderDocumentList = () => {
    return (
      <>
        {documents.length === 0 ? (
          <DocumentListEmptyContainer data-testid='documents-subcategory-empty-list'>
            <ContentNotAvailable
              iconClass='mdi-file-multiple-outline light-icon'
              datatestid='documents-subcategory-empty-list-content'
            >
              You haven't submitted {''}
              {documentProcessing.selectedDocumentCategory?.name?.toLocaleLowerCase()}
            </ContentNotAvailable>
          </DocumentListEmptyContainer>
        ) : (
          <DocumentsFolderList data-testid='documents-subcategory-list'>
            {documents.map((document, i) => renderDocument(document, i))}
          </DocumentsFolderList>
        )}
      </>
    )
  }

  const loadings = loading

  const loadingDelay = useLoadingDelay(loadings)

  if (error) {
    return <div>{error?.message}</div>
  }

  function handleSummaryClick(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    e.preventDefault()
    history.push('/dashboard/documents/submission-summary')
  }

  return (
    <>
      <GoBackHeader text={goBackHeaderName} />
      {loadingDelay && loadingSpinnerPortal()}
      <DocumentsFolderContainer>
        <h2 data-testid='documents-subcategory-title'>
          {documentProcessing.selectedDocumentChecklistItem?.name}
        </h2>
        {showSummaryButton && (
          <Button
            variant='outline-secondary'
            data-testid='document-summary-button'
            type='button'
            onClick={handleSummaryClick}
          >
            Summary{' '}
            {showErrorIcon && (
              <span
                className='mdi mdi-alert-circle'
                style={{ color: 'red' }}
              ></span>
            )}
          </Button>
        )}

        {renderDocumentList()}
      </DocumentsFolderContainer>
    </>
  )
}

export default AiDocumentsChecklistItem
